import { AfterViewInit, Directive, effect, ElementRef, HostListener, inject, input, InputSignal, OnDestroy, Signal, WritableSignal } from '@angular/core';
import { BooleanInputType, toBooleanProperty } from '@utils/boolean/transform';
import { stopAndPreventEvent } from '@utils/dom/event';
import { uuid } from '@utils/string';
import { Placement } from 'tippy.js';
import { TippyInstance, TOOLTIP_DEFAULT_OPTIONS, TooltipAnimation, TooltipService, TooltipTheme } from './tooltip.service';

export type InputAndWritableAndSignal<T> = InputSignal<T> | WritableSignal<T> | Signal<T>;

export const LIGHT_THEME_DEFAULT_DELAY: [number, number] = [2500, 2500] as const;

@Directive({
  selector: '[mirTooltip]',
  standalone: true
})
export class TooltipDirective implements AfterViewInit, OnDestroy {

  @HostListener('mouseenter', ['$event']) mouseEnter(event: MouseEvent) { this.handleMouseEnter(event); }

  @HostListener('mouseleave', ['$event']) mouseLeave(event: MouseEvent) { this.handleMouseLeave(event); }

  /** @Input */
  readonly tooltip = input<string>('');
  readonly disable = input(false, { transform: (value: BooleanInputType) => toBooleanProperty(value) });
  readonly theme = input<TooltipTheme>(TOOLTIP_DEFAULT_OPTIONS.theme as TooltipTheme);
  readonly allowHtml = input(TOOLTIP_DEFAULT_OPTIONS.allowHTML, { transform: (value: BooleanInputType) => toBooleanProperty(value) });
  readonly placement = input<Placement>(TOOLTIP_DEFAULT_OPTIONS.placement as Placement);
  readonly offset = input<[number, number]>(TOOLTIP_DEFAULT_OPTIONS.offset as [number, number]);
  readonly animation = input<TooltipAnimation>(TOOLTIP_DEFAULT_OPTIONS.animation as boolean);
  readonly delay = input<[number, number] | number>(TOOLTIP_DEFAULT_OPTIONS.delay as [number, number]);
  readonly duration = input<[number, number] | number>(TOOLTIP_DEFAULT_OPTIONS.duration as [number, number]);
  readonly arrow = input(TOOLTIP_DEFAULT_OPTIONS.arrow, { transform: (value: BooleanInputType) => toBooleanProperty(value) });
  readonly interactive = input(TOOLTIP_DEFAULT_OPTIONS.interactive, { transform: (value: BooleanInputType) => toBooleanProperty(value) });

  private readonly service = inject(TooltipService);
  readonly #elementRef = inject(ElementRef);

  #tippyInstance: TippyInstance;
  readonly #uid = uuid();

  constructor() {
    effect(() => {
      const disable = this.disable();
      const tooltip = this.tooltip();
      const offset = this.offset();
      const theme = this.theme();
      const duration = this.duration();
      let delay = this.delay();
      const element = this.#elementRef.nativeElement as HTMLElement | null;

      if (this.#tippyInstance) {
        if (disable) {
          this.service.disable(this.#tippyInstance);
        } else {
          this.service.enable(this.#tippyInstance);
        }
        if (tooltip) { this.#tippyInstance['setContent'](tooltip); }

        this.#tippyInstance.setProps({ offset, delay, duration, theme });

        // If tooltip was hidden during scroll while the pointer stayed still,
        // re-show it automatically once re-enabled and still hovered.
        if (!disable && tooltip && element?.matches(':hover')) {
          this.service.showTooltip(this.#tippyInstance);
        }
      } else {
        if (element) {
          this.attachTooltip(element);
        }
      }
    });
  }

  ngAfterViewInit() {
    const element = this.#elementRef.nativeElement;
    if (element) { this.attachTooltip(element); }
  }

  ngOnDestroy() {
    if (this.#tippyInstance) {
      this.service.destroy(this.#tippyInstance);
      this.#tippyInstance = null;
    }
  }

  private handleMouseEnter(event: MouseEvent) {
    stopAndPreventEvent(event);
    this.service.showTooltip(this.#tippyInstance);
  }

  private handleMouseLeave(event: MouseEvent) {
    stopAndPreventEvent(event);
    this.service.hideTooltip(this.#tippyInstance);
  }

  private attachTooltip(element: any) {
    const tooltip = this.tooltip();
    const theme = this.theme();
    const placement = this.placement();
    const offset = this.offset();
    const animation = this.animation();
    const allowHTML = this.allowHtml();
    const arrow = this.arrow();
    const interactive = this.interactive();
    const duration = this.duration();
    const delay = this.delay();

    this.#tippyInstance = this.service.createTooltip(
      this.#uid,
      element,
      tooltip,
      {
        theme,
        placement,
        offset,
        animation,
        allowHTML,
        arrow,
        interactive,
        // ⚠️ not well injected - for show delay, see service
        delay,
        duration,
      }
    );
  }
}
